<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="clientLoader === true || loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-2"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon
            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- Invoice: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>
              <!-- Field: Clients -->
              <b-col
                cols="12"
                md="4"
              >
                <b-button
                  id="clients"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  v-b-modal.selectClientModal
                  variant="outline-primary"
                  class="m-2"
                  block
                >
                  {{ $t('DEBTIA_SELECT_CLIENT') }}
                </b-button>
                <span
                  v-if="client === '' && clientErrorMsg !== ''"
                  class="error-msg m-2"
                >Client is not selected</span>
              </b-col>

              <!-- Field: Invoice Date -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="errors"
                  name="Invoice Date"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_INVOICE_DATE')"
                    label-for="invoice_date"
                  >
                    <b-input-group>
                      <cleave
                        id="invoice_date"
                        v-model="datesArr[0].cleaveFormat"
                        class="form-control"
                        :raw="false"
                        :options="options.date"
                        placeholder="DD-MM-YYYY"
                        @input="validateDate(0, datesArr[0].cleaveFormat, 0, true), setDueDate(datesArr[0].cleaveFormat)"
                      />
                      <b-input-group-append>
                        <b-form-datepicker
                          id="invoice_date"
                          v-model="datesArr[0].datePicketFormat"
                          button-only
                          right
                          :date-format-options="{ day: 'numeric', month: 'numeric', year: 'numeric' }"
                          trim
                          size="sm"
                          placeholder="DD-MM-YYYY"
                          :state="errors.length > 0 ? false : null"
                          :min="YMDdateFormat(minDate)"
                          @input="validateDate(1, datesArr[0].datePicketFormat, 0, true), setDueDate(datesArr[0].cleaveFormat)"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <label
                      id="invoice_date_error"
                      style="color: red; display:none;"
                    >*{{ $t('DEBTIA_DATE_FORMAT_ERROR') }}</label>
                    <small class="text-danger">{{ errors[0] }}</small></b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Due Date -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Due Date"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_DUE_DATE')"
                    label-for="due_date"
                  >
                    <b-input-group>
                      <cleave
                        id="due_date"
                        v-model="datesArr[1].cleaveFormat"
                        class="form-control"
                        :raw="false"
                        :options="options.date"
                        placeholder="DD-MM-YYYY"
                        @input="validateDate(0, datesArr[1].cleaveFormat, 1, false)"
                      />
                      <b-input-group-append>
                        <b-form-datepicker
                          id="due_date"
                          v-model="datesArr[1].datePicketFormat"
                          button-only
                          right
                          :date-format-options="{ day: 'numeric', month: 'numeric', year: 'numeric' }"
                          trim
                          size="sm"
                          placeholder="DD-MM-YYYY"
                          :state="errors.length > 0 ? false:null"
                          :min="YMDdateFormat(minDueDate)"
                          @input="validateDate(1, datesArr[1].datePicketFormat, 1, false)"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <label
                      id="due_date_error"
                      style="color: red; display:none;"
                    >*{{ $t('DEBTIA_DATE_FORMAT_ERROR') }}</label>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group></validation-provider>
              </b-col>

              <b-col
                v-if="client !== ''"
                cols="12"
                xl="12"
                md="12"
              >
                <b-card
                  no-body
                  class="border mt-1"
                >
                  <div class="d-flex flex-column mt-0">

                    <!-- Header: Client Info -->
                    <div
                      v-if="typeof clientAddress === 'object'"
                      class="m-1"
                    >
                      <div>
                        <h3 class="text-primary">
                          {{ $t('DEBTIA_CLIENT_INFORMATION') }}
                        </h3>
                      </div>
                      <p class="card-text mb-25">
                        {{ clientObj.name }}
                      </p>
                      <p class="card-text mb-25">
                        {{ clientAddress.road_name }} {{ clientAddress.house_no }}
                      </p>
                      <p class="card-text mb-0">
                        {{ clientAddress.zip_code }} {{ clientAddress.city }}
                      </p>
                      <p class="card-text mb-0">
                        {{ clientAddress.country }}
                      </p>
                      <p
                        v-if="clientObj.contact_person !== null && clientObj.contact_person !== ''"
                        class="card-text mb-0"
                      >
                        Att: {{ clientObj.contact_person }}
                      </p>
                      <p
                        v-if="(clientObj.type !== 'person' && clientObj.cvrcpr !== null)"
                        class="card-text mb-0"
                      >
                        {{ clientObj.cvrcpr }}
                      </p>
                      <div class="right">
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          v-b-modal.clientInfoModal
                          size="sm"
                          variant="danger"
                          class="m-1"
                          @click="changeInfo('edit')"
                        >
                          {{ $t('DEBTIA_EDIT_INFO') }}
                        </b-button>
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          v-b-modal.selectClientModal
                          size="sm"
                          variant="primary"
                          class="m-1"
                        >
                          {{ $t('DEBTIA_CHANGE') }}
                        </b-button>
                      </div>
                    </div>
                    <div v-else>
                      <p
                        class="card-text mb-25"
                        style="color: red;"
                      >
                        Client Address Not Available
                      </p>
                    </div>
                  </div>
                </b-card>
              </b-col>

              <!-- Field: Note -->
              <b-col
                cols="12"
                md="12"
              >
                <b-form-group
                  :label="$t('DEBTIA_NOTE')"
                  label-for="note"
                >
                  <b-form-textarea
                    id="note"
                    v-model="note"
                    placeholder="Note"
                    rows="5"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <!-- PRODUCTS TABLE -->
            <b-card
              no-body
              class="border mt-1"
            >
              <b-card-header class="p-1">
                <b-card-title class="font-medium-2">
                  <feather-icon
                    icon="BoxIcon"
                    size="18"
                  />
                  <span class="align-middle ml-50">{{ $t('DEBTIA_PRODUCTS') }}</span>
                </b-card-title>
              </b-card-header>
              <b-form-group>
                <b-table
                  v-if="!loader"
                  ref="roles"
                  class="position-relative"
                  :fields="fields"
                  :items="items"
                  responsive
                >
                  <template
                    #cell(removeIcon)="data"
                  >
                    <b-button
                      v-if="data.index > 0"
                      variant="gradient-danger"
                      class="btn-icon rounded-circle"
                      size="sm"
                      @click="removeProduct(data)"
                    >
                      <feather-icon icon="Trash2Icon" />
                    </b-button>
                  </template>
                  <template #cell(name)="data">
                    <v-select
                      v-model="data.item.productId"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="data.item.name"
                      append-to-body
                      :reduce="val => val.value"
                      :clearable="false"
                      :input-id="`products-${data.index}`"
                      @input="getProductPrice($event, data)"
                    />
                  </template>

                  <template #cell(quantity)="data">
                    <span class="text-nowrap">
                      <validation-provider
                        #default="validationContext"
                        name="Quantity"
                        rules="required|positive"
                      >
                        <b-form-input
                          v-model="data.item.quantity"
                          placeholder="Quantity"
                          type="number"
                          required
                          @input="getSubTotal"
                        />
                        <small class="text-danger">{{ validationContext.errors[0] }}</small>
                      </validation-provider>
                    </span>
                  </template>

                  <template #cell(price)="data">
                    <span class="text-nowrap">
                      <validation-provider
                        #default="validationContext"
                        name="Price"
                        rules="required|min_value:1"
                      >
                        <b-form-input
                          v-model="data.item.price"
                          placeholder="Price"
                          type="number"
                          required
                          step="0.01"
                          @input="getSubTotal"
                        />
                        <small class="text-danger">{{ validationContext.errors[0] }}</small>
                      </validation-provider>
                    </span>
                  </template>

                  <template #cell(discount)="data">
                    <span class="text-nowrap">
                      <validation-provider
                        #default="validationContext"
                        name="Discount"
                        rules="required|positive|max-value:100"
                      >
                        <b-form-input
                          v-model="data.item.discount"
                          placeholder="Discount"
                          type="number"
                          step="0.01"
                          required
                          @input="getSubTotal"
                        />
                        <small class="text-danger">{{ validationContext.errors[0] }}</small>
                      </validation-provider>
                    </span>
                  </template>

                  <template #cell(total)="data">
                    <span class="text-nowrap">{{ new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat((data.item.price * data.item.quantity * (1-(data.item.discount / 100)))).toFixed(2)) }}</span>
                  </template>
                </b-table>
              </b-form-group>

              <b-form-group
                class="text-center"
              >
                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-primary"
                  class="btn-icon rounded-circle"
                  @click="addProduct"
                >
                  <feather-icon
                    icon="PlusIcon"
                  />
                </b-button>
              </b-form-group>
              <hr>
              <b-form-group class="p-2">
                <b-row>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_TEMPLATE')"
                    >
                      <b-form-input
                        readonly
                        value="Invoice Letter Template"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group
                      :label="$t('DEBTIA_PROCESS_AFTER_MISSED_PAYMENT')"
                    >
                      <b-form-input
                        readonly
                        value="Create Debt Case"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <div class="left">
                      <p>
                        <small>{{ $t('DEBTIA_SUBTOTAL') }}</small>
                        <br>
                        <small>VAT (25%)</small>
                        <br>
                        <small>{{ $t('DEBTIA_TOTAL') }}</small>
                      </p>
                    </div>
                    <div class="right">
                      <p>
                        <small>{{ formatCurrency(subTotal, defaultCurrency) }}</small>
                        <br>
                        <small>{{ formatCurrency(vat, defaultCurrency) }}</small>
                        <br>
                        <small>{{ formatCurrency(total, defaultCurrency) }}</small>
                      </p>
                    </div>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-card>
            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>

        </validation-observer>
        <div v-if="client !== '' && modalLoader === true">
          <client-modal
            :client-data="clientObj"
            :modal-loads="modalLoader"
            :client-address="clientAddress"
            @update-data="handleClientData"
          />
        </div>

        <select-client-modal
          :clients-data="clientOptions"
          :loader="clientLoader"
          @selected-client="selectClient"
          @search-clients="getClients"
        />

      </b-card>
    </b-overlay>
  </div>

</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BTable, BCard, BCardHeader, BCardTitle, BOverlay, BAlert, BFormDatepicker, BFormTextarea, BInputGroupAppend, BInputGroup,
} from 'bootstrap-vue'
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from '@axios'
import Cleave from 'vue-cleave-component'
import { min_value } from 'vee-validate/dist/rules'
import formValidation from '@core/comp-functions/forms/form-validation'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Ripple from 'vue-ripple-directive'
import ClientModal from './ClientModal.vue'
import SelectClientModal from './SelectClientModal.vue'

extend('min_value', min_value)

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BForm,
    BTable,
    BCard,
    BCardHeader,
    BCardTitle,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BOverlay,
    BAlert,
    BFormDatepicker,
    ClientModal,
    SelectClientModal,
    BInputGroup,
    BInputGroupAppend,
    Cleave,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      alertErrors: [],
      min_value,
      client: '',
      minDate: new Date().toISOString().split('T')[0].split('-').reverse().join('-'),
      minDueDate: '',
      fields: [
        { key: 'removeIcon', label: '' },
        { key: 'name', label: 'Product', thStyle: { width: '300px' } },
        { key: 'quantity', label: 'Quantity' },
        { key: 'price', label: 'Price' },
        { key: 'discount', label: 'Discount %' },
        { key: 'total', label: 'Total' },
      ],
      items: [],
      subTotal: 0.00,
      vat: 0,
      total: 0.00,
      productsObj: [],
      note: '',
      clientErrorMsg: '',
      breakLoop: false,
      clientAddress: null,
      clientObj: null,
      modalLoader: false,
      clientLoader: true,
      options: {
        date: {
          date: true,
          delimiter: '-',
          datePattern: ['d', 'm', 'Y'],
        },
      },
      dateErr: false,
      datesArr: [
        // 👇 index 0 is for invoice date
        {
          cleaveFormat: new Date().toISOString().split('T')[0].split('-').reverse().join('-'),
          datePicketFormat: new Date().toISOString().split('T')[0],
        },
        // 👇 index 1 is for due date, due date will be auto set that's why they're kept empty
        {
          cleaveFormat: '',
          datePicketFormat: '',
        },
      ],
      defaultCurrency: localStorage.getItem('defaultCurrency') || 'DKK',
    }
  },
  setup() {
    const clientOptions = []
    const loading = true
    const loader = true
    const resetData = () => {
      // console.log()
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetData)

    return {
      clientOptions,
      loading,
      loader,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
  async created() {
    this.getClients()
    await this.getProducts()
    this.addProduct()
  },
  methods: {
    YMDdateFormat(value) {
      const parts = value.split('-')
      const day = Number(parts[0])
      const month = Number(parts[1])
      const year = Number(parts[2])
      const formattedDate = `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`
      return formattedDate
    },
    DMYdateFormat(value) {
      const parts = value.split('-')
      const year = Number(parts[0])
      const month = Number(parts[1])
      const day = Number(parts[2])
      const formattedDate = `${day < 10 ? '0' : ''}${day}-${month < 10 ? '0' : ''}${month}-${year}`
      return formattedDate
    },
    validateDate(type, value, index, invoiceDate) {
      this.dateErr = false
      if (type === 0) {
        const regexPattern = /^\d{4}-\d{2}-\d{2}$/
        const fDate = this.YMDdateFormat(value)
        if (regexPattern.test(fDate)) {
          // 👇 this check whether the dates are entered in correct order
          if (!this.dateValidation(fDate, invoiceDate)) {
            return false
          }
          this.datesArr[index].datePicketFormat = fDate
        } else {
          this.datesArr[index].datePicketFormat = ''
          this.dateErr = true
        }
      } else {
        const regexPattern = /^\d{2}-\d{2}-\d{4}$/
        const fDate = this.DMYdateFormat(value)
        if (regexPattern.test(fDate)) {
          // 👇 this check whether the dates are entered in correct order
          if (!this.dateValidation(value, invoiceDate)) {
            return false
          }
          this.datesArr[index].cleaveFormat = fDate
        } else {
          this.datesArr[index].cleaveFormat = ''
          this.dateErr = true
        }
      }
      return true
    },
    // 👇 this function check whether the dates are entered in correct order
    dateValidation(value, invoiceDate) {
      const minDate = invoiceDate === true ? this.YMDdateFormat(this.minDate) : this.YMDdateFormat(this.minDueDate)
      const errorElementId = invoiceDate === true ? 'invoice_date_error' : 'due_date_error'
      const errorMessage = `Please add date after or equal to ${this.YMDdateFormat(minDate)}`
      const errorElement = document.getElementById(errorElementId)

      // 👇 this will if the due date is in future or not
      if (!invoiceDate && this.datesArr[1].datePicketFormat !== '' && this.datesArr[1].datePicketFormat < this.datesArr[0].datePicketFormat) {
        errorElement.innerHTML = 'Due Date must be greater than Invoice Date'
        errorElement.style.display = 'block'
        this.dateErr = true
        return false
      }

      // 👇 this will check that the entered dates should not be in the past
      if (value < minDate) {
        errorElement.innerHTML = errorMessage
        errorElement.style.display = 'block'
        this.dateErr = true
        return false
      }
      errorElement.style.display = 'none'
      return true
    },
    // eslint-disable-next-line consistent-return
    async onSubmit() {
      if (this.client === '') {
        this.clientErrorMsg = 'Client is not selected'
        return false
      }
      this.alertErrors = []
      this.clientErrorMsg = ''
      await this.getClients(this.client)
      if (this.clientOptions.length === 0) {
        this.loader = false
        this.alertErrors.push('Invalid client selected.')
        return false
      }
      const clientObj = this.clientOptions
      const [client] = clientObj
      if (this.dateErr) {
        this.loader = false
        this.alertErrors.push('Please add correct Dates.')
        return false
      }
      this.breakLoop = false
      const products = this.items.map((e, i) => {
        if (e.productName === null) {
          const selector = `#products-${i}`
          const elementHTML = this.$el.querySelector(selector)
          const ancestorWithClass = elementHTML.closest('.vs__dropdown-toggle')
          ancestorWithClass.classList.add('inputErrorClassField')
          this.breakLoop = true
        }
        return {
          no: e.productName,
          quantity: parseFloat(e.quantity),
          unit_price: parseFloat(e.price),
          discount: parseFloat(e.discount),
          description: e.description,
        }
      })
      if (this.breakLoop) {
        this.loader = false
        return false
      }
      this.loader = true
      const data = {
        client_id: this.client,
        client_cvr: client.cvrcpr,
        invoice_date: this.datesArr[0].datePicketFormat,
        due_date: this.datesArr[1].datePicketFormat,
        notes: this.note,
        house_no: client.client_address[0].house_no,
        roadname: client.client_address[0].road_name,
        postcode: client.client_address[0].zip_code,
        city: client.client_address[0].city,
        products,
      }
      await axios.post('invoice-system/create-invoice', { ...data })
        .then(() => {
          this.$router.push('/invoices')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Invoice Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Invoice successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          let toastError = error.response.data.message
          if (typeof error.response.data.message === 'object') {
            this.alertErrors.push(...error.response.data.message)
            toastError = 'Something went wrong'
          } else {
            this.alertErrors.push(error.response.data.data.error.message)
          }
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error..!',
              icon: 'CoffeeIcon',
              variant: 'danger',
              text: toastError,
            },
          })
          this.loader = false
        })
    },
    async getProducts() {
      try {
        this.loader = true
        const getProducts = await axios.get('/invoice-system/get-products')
        getProducts.data.data.map(e => (this.productsObj.push({ ...e, label: e.description, value: e.id })))
        this.loader = false
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Clients',
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: e.message,
          },
        })
      }
    },
    addProduct() {
      const products = {
        name: [],
        quantity: 1,
        price: 0.00,
        discount: 0.00,
        total: 0.00,
        productId: 0,
        productName: null,
        description: null,
      }
      this.productsObj.forEach(element => {
        products.name.push({ label: `${element.description}(${element.no})`, value: element.id })
      })
      this.items.push(products)
    },
    getProductPrice(e, data) {
      const selector = `#products-${data.index}`
      const elementHTML = this.$el.querySelector(selector)
      const ancestorWithClass = elementHTML.closest('.vs__dropdown-toggle')
      ancestorWithClass.classList.remove('inputErrorClassField')
      const productID = data.item.name[e - 1].value
      const { index } = data
      const price = this.productsObj.filter(el => {
        if (el.id === productID) return el
        return null
      })
      this.items[index].price = parseFloat(price[0].unit_price)
      this.items[index].productName = price[0].no
      this.items[index].description = price[0].description
      this.getSubTotal()
    },
    getSubTotal() {
      this.subTotal = 0
      this.items.forEach(element => {
        this.subTotal += element.quantity * element.price * (1 - element.discount / 100)
      })
      this.subTotal = parseFloat(this.subTotal.toFixed(2))
      this.vat = (this.subTotal * 0.25).toFixed(2)
      this.total = (parseFloat(this.subTotal) + parseFloat(this.vat)).toFixed(2)
    },
    removeProduct(d) {
      this.items.splice(d.index, 1)
      this.getSubTotal()
    },
    formatCurrency(amount, defaultCurrency) {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(amount).toFixed(2))
    },
    // async getClients() {
    //   try {
    //     this.clientLoader = true
    //     const getClients = await axios.get('/clients/get-clients?perPage=100')
    //     getClients.data.data.data.data.map(e => (this.clientOptions.push({
    //       ...e, label: e.name, value: e.id, email: e.billing_email ? e.billing_email : e.email,
    //     })))
    //     this.clientLoader = false
    //   } catch (e) {
    //     this.$toast({
    //       component: ToastificationContent,
    //       position: 'top-right',
    //       props: {
    //         title: 'Clients',
    //         icon: 'CoffeeIcon',
    //         variant: 'danger',
    //         text: e.message,
    //       },
    //     })
    //   }
    // },
    async getClients(search = '') {
      try {
        this.clientLoader = true
        const response = await axios.get('/clients/get-clients', {
          params: {
            perPage: 100,
            search, // Pass the search string as a query parameter
          },
        })
        this.clientOptions = response.data.data.data.data.map(client => ({
          ...client,
          label: client.name,
          value: client.id,
          email: client.billing_email || client.email,
        }))
        this.clientLoader = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Clients',
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: error.message,
          },
        })
      }
    },
    selectClient(data) {
      this.modalLoader = false
      const clientObj = this.clientOptions.filter(e => e.id === data.id)
      const [client] = clientObj
      this.clientObj = client
      this.client = data.id
      const billingAddress = client.client_address.filter(e => e.address_type === 'billing')
      const [address] = billingAddress.length > 0 ? billingAddress : client.client_address
      this.clientAddress = address
    },
    setDueDate(val) {
      if (val !== '') {
        const regex = /^\d{2}-\d{2}-\d{4}$/
        if (regex.test(val)) {
          // Step 1: Parse the date string
          const dateParts = val.split('-')
          const date = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
          // Step 2: Add 10 days to the date
          date.setDate(date.getDate() + 10)
          // Step 3: Format the updated date back to D-M-Y
          const formattedDate = date.toISOString().split('T')[0].split('-').reverse().join('-')
          const [YMDFormat] = date.toISOString().split('T')
          // Step 4: Update the due_date and minDueDate new Date().toISOString().split('T')[0]
          if (this.datesArr[0].datePicketFormat >= this.YMDdateFormat(this.minDate)) {
            this.datesArr[1].cleaveFormat = formattedDate
            this.datesArr[1].datePicketFormat = YMDFormat
            this.minDueDate = formattedDate
          }
        }
      }
    },
    changeInfo() {
      this.modalLoader = true
    },
    handleClientData(data) {
      this.clientAddress = data.address
      this.clientObj = data.client
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}
.left{
  float: left;
}
.right{
  float: right;
}
.inputErrorClassField{
  border-color: red !important;
}
.error-msg {
  color: #f36c6c;
}

.v-select .vs__selected {
  white-space: normal;
  overflow: visible;
  height: auto;
  min-height: 50px;
  padding: 5px;
  line-height: 1.5;
}

.v-select {
  min-height: 50px;
  height: auto;
  box-sizing: border-box;
}

.vs__dropdown-menu .vs__dropdown-option {
  white-space: normal;
  line-height: 1.5;
  padding: 10px;
}

.vs__dropdown-menu {
  z-index: 9999;
  position: absolute;
  top: 100%;
}

</style>
